import { Radio, Text } from '@mantine/core';
import { clsx } from 'clsx';
import { FormattedNumber, useIntl } from 'react-intl';

import styles from './RateSelector.module.css';
import { freightRateCardMessages } from '../../../freight-rate-card/freight-rate-card-messages';

import type { LtlQuote } from '../../../../types/ltl';

type RateSelectorProps = {
  quote: LtlQuote | null;
  label: string;
  isSelected: boolean;
  onSelect: (quote: LtlQuote) => void;
};

export const RateSelector = ({ quote, label, isSelected, onSelect }: RateSelectorProps) => {
  const intl = useIntl();

  const handleSelect = () => {
    if (quote) {
      onSelect(quote);
    }
  };

  if (!quote) {
    return (
      <div aria-disabled className={clsx(styles.selector, styles.selectorUnavailable)} data-testid='rate-selector-unavailable'>
        <Radio aria-label={label} checked={false} disabled readOnly size='xs' tabIndex={-1} />
        <Text className={styles.unavailable} component='span'>
          {intl.formatMessage(freightRateCardMessages.notAvailable, { label })}
        </Text>
      </div>
    );
  }

  return (
    <div
      aria-pressed={isSelected}
      className={clsx(styles.selector, isSelected && styles.selectorSelected)}
      data-testid={`rate-selector-${quote.offerId}`}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      role='button'
      tabIndex={0}
    >
      <Radio aria-label={label} checked={isSelected} onChange={handleSelect} readOnly size='xs' tabIndex={-1} />
      <div className={styles.middleRow}>
        <Text className={styles.price} component='span' size='sm'>
          <FormattedNumber currency='USD' style='currency' value={quote.totalCharges} />
        </Text>
        <Text className={styles.transit} component='span' size='sm'>
          {intl.formatMessage(freightRateCardMessages.transitDays, { days: quote.transitDays })}
        </Text>
      </div>
    </div>
  );
};
